import React, { useState, useEffect } from 'react';

const PaymentMethodsPage = () => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCard, setShowAddCard] = useState(false);
  const [newCard, setNewCard] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    isDefault: false
  });

  useEffect(() => {
    // Simulate API call to fetch saved payment methods
    setTimeout(() => {
      setPaymentMethods([
        {
          id: 1,
          type: 'visa',
          last4: '1234',
          expiryMonth: '12',
          expiryYear: '2028',
          cardholderName: 'John Doe',
          isDefault: true,
          addedDate: '2024-01-15'
        },
        {
          id: 2,
          type: 'mastercard',
          last4: '5678',
          expiryMonth: '08',
          expiryYear: '2027',
          cardholderName: 'John Doe',
          isDefault: false,
          addedDate: '2024-02-20'
        }
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  const handleAddCard = (e) => {
    e.preventDefault();
    
    // Simulate adding new card
    const cardType = getCardType(newCard.cardNumber);
    const newPaymentMethod = {
      id: Date.now(),
      type: cardType,
      last4: newCard.cardNumber.slice(-4),
      expiryMonth: newCard.expiryDate.split('/')[0],
      expiryYear: '20' + newCard.expiryDate.split('/')[1],
      cardholderName: newCard.cardholderName,
      isDefault: newCard.isDefault,
      addedDate: new Date().toISOString().split('T')[0]
    };

    setPaymentMethods(prev => {
      const updated = newCard.isDefault 
        ? prev.map(method => ({ ...method, isDefault: false }))
        : prev;
      return [...updated, newPaymentMethod];
    });

    // Reset form
    setNewCard({
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardholderName: '',
      isDefault: false
    });
    setShowAddCard(false);
  };

  const handleDeleteCard = (cardId) => {
    if (window.confirm('Are you sure you want to delete this payment method?')) {
      setPaymentMethods(prev => prev.filter(method => method.id !== cardId));
    }
  };

  const handleSetDefault = (cardId) => {
    setPaymentMethods(prev => prev.map(method => ({
      ...method,
      isDefault: method.id === cardId
    })));
  };

  const getCardType = (number) => {
    const num = number.replace(/\s/g, '');
    if (/^4/.test(num)) return 'visa';
    if (/^5[1-5]/.test(num)) return 'mastercard';
    if (/^3[47]/.test(num)) return 'amex';
    return 'unknown';
  };

  const getCardIcon = (type) => {
    switch (type) {
      case 'visa':
        return '💳';
      case 'mastercard':
        return '💳';
      case 'amex':
        return '💳';
      default:
        return '💳';
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
          <p className="text-jet-gray-600">Loading payment methods...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-jet-gray-900">Payment Methods</h1>
          <p className="text-jet-gray-600">Manage your saved payment methods for faster checkout</p>
        </div>
        
        <button
          onClick={() => setShowAddCard(true)}
          className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium px-4 py-2 rounded-md transition-colors"
        >
          Add New Card
        </button>
      </div>

      {/* Saved Payment Methods */}
      <div className="space-y-4 mb-8">
        {paymentMethods.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="text-6xl mb-4">💳</div>
            <h3 className="text-lg font-medium text-jet-gray-900 mb-2">No payment methods saved</h3>
            <p className="text-jet-gray-500 mb-4">Add a payment method to make booking faster and easier</p>
            <button
              onClick={() => setShowAddCard(true)}
              className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium px-6 py-3 rounded-md transition-colors"
            >
              Add Your First Card
            </button>
          </div>
        ) : (
          paymentMethods.map((method) => (
            <div key={method.id} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="text-3xl">{getCardIcon(method.type)}</div>
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-jet-gray-900 capitalize">
                        {method.type} •••• {method.last4}
                      </span>
                      {method.isDefault && (
                        <span className="bg-jet-green-100 text-jet-green-800 text-xs font-medium px-2 py-1 rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-jet-gray-600">
                      {method.cardholderName} • Expires {method.expiryMonth}/{method.expiryYear}
                    </div>
                    <div className="text-xs text-jet-gray-500">
                      Added on {new Date(method.addedDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {!method.isDefault && (
                    <button
                      onClick={() => handleSetDefault(method.id)}
                      className="text-jet-blue-600 hover:text-jet-blue-700 text-sm font-medium"
                    >
                      Set as Default
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleDeleteCard(method.id)}
                    className="text-jet-red-600 hover:text-jet-red-700 text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add New Card Modal */}
      {showAddCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-jet-gray-900">Add New Card</h3>
              <button
                onClick={() => setShowAddCard(false)}
                className="text-jet-gray-400 hover:text-jet-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddCard} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                  Cardholder Name *
                </label>
                <input
                  type="text"
                  value={newCard.cardholderName}
                  onChange={(e) => setNewCard(prev => ({ ...prev, cardholderName: e.target.value }))}
                  className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
                  placeholder="Enter cardholder name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                  Card Number *
                </label>
                <input
                  type="text"
                  value={newCard.cardNumber}
                  onChange={(e) => setNewCard(prev => ({ ...prev, cardNumber: formatCardNumber(e.target.value) }))}
                  className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
                  placeholder="1234 5678 9012 3456"
                  maxLength="19"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                    Expiry Date *
                  </label>
                  <input
                    type="text"
                    value={newCard.expiryDate}
                    onChange={(e) => setNewCard(prev => ({ ...prev, expiryDate: formatExpiryDate(e.target.value) }))}
                    className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
                    placeholder="MM/YY"
                    maxLength="5"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                    CVV *
                  </label>
                  <input
                    type="text"
                    value={newCard.cvv}
                    onChange={(e) => setNewCard(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, '') }))}
                    className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
                    placeholder="123"
                    maxLength="4"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="setDefault"
                  checked={newCard.isDefault}
                  onChange={(e) => setNewCard(prev => ({ ...prev, isDefault: e.target.checked }))}
                  className="mr-2 h-4 w-4 text-jet-yellow focus:ring-jet-yellow border-jet-gray-300 rounded"
                />
                <label htmlFor="setDefault" className="text-sm text-jet-gray-700">
                  Set as default payment method
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddCard(false)}
                  className="flex-1 border border-jet-gray-300 hover:border-jet-gray-400 text-jet-gray-700 font-medium py-2 px-4 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium py-2 px-4 rounded-md transition-colors"
                >
                  Add Card
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Security Information */}
      <div className="bg-jet-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Security & Privacy</h3>
        
        <div className="space-y-3 text-sm">
          <div className="flex items-start">
            <div className="w-2 h-2 bg-jet-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
            <div>
              <div className="font-medium text-jet-gray-900">Secure Storage</div>
              <div className="text-jet-gray-600">
                Your payment information is encrypted and stored securely using industry-standard security measures.
              </div>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-2 h-2 bg-jet-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
            <div>
              <div className="font-medium text-jet-gray-900">PCI Compliance</div>
              <div className="text-jet-gray-600">
                We are PCI DSS compliant and follow strict security protocols to protect your data.
              </div>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-2 h-2 bg-jet-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
            <div>
              <div className="font-medium text-jet-gray-900">No CVV Storage</div>
              <div className="text-jet-gray-600">
                For your security, we never store your CVV code. You'll need to enter it for each transaction.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodsPage;